import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Alert, Card, Spinner, Form, Badge } from 'react-bootstrap';
import authService from '../services/authService';
import BackToDashboard from '../components/BackToDashboard';

function BrandTexture() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedWords, setSelectedWords] = useState([]); // User's custom selections (max 3)
  const [archetypeTextures, setArchetypeTextures] = useState([]); // Archetype-recommended textures
  const [selectedArchetypeTextures, setSelectedArchetypeTextures] = useState([]); // Which archetype textures are selected
  const [archetypeName, setArchetypeName] = useState('');
  const [customWord, setCustomWord] = useState('');
  const [saving, setSaving] = useState(false);

  const textureOptions = [
    'Delicate',
    'Lacy',
    'Smooth',
    'Glossy',
    'Metallic',
    'Silky',
    'Soft',
    'Velvety',
    'Rough',
    'Gritty',
    'Grainy',
    'Leathery',
    'Organic',
    'Plain',
    'Earthy',
    'Marbled',
    'Glittery',
    'Sparkly',
    'Frosted',
    'Energetic',
    'Crystalline',
    'Rich / Expensive',
    'Plastic'
  ];

  useEffect(() => {
    initializePage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializePage = async () => {
    setLoading(true);

    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    // Load saved selections
    await fetchSavedTexture();

    setLoading(false);
  };

  const fetchSavedTexture = async () => {
    try {
      const response = await fetch('/api/brand/get-saved-brand-texture', {
        method: 'GET',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();

        // Set archetype info
        if (data.archetype_textures) {
          setArchetypeTextures(data.archetype_textures);
          // By default, all archetype textures are selected
          setSelectedArchetypeTextures(data.archetype_textures);
        }
        if (data.archetype_name) {
          setArchetypeName(data.archetype_name);
        }

        // Set saved custom selections
        if (data.brand_texture && data.brand_texture.selected_words) {
          const savedWords = data.brand_texture.selected_words;
          // Separate archetype textures from custom selections
          const customSelections = savedWords.filter(w => !data.archetype_textures.includes(w));
          const archetypeSelections = savedWords.filter(w => data.archetype_textures.includes(w));

          setSelectedWords(customSelections);
          setSelectedArchetypeTextures(archetypeSelections);
        }
      }
      // If 404, no saved texture - that's okay
    } catch (err) {
      console.log('No saved texture found or error fetching:', err);
    }
  };

  const toggleWord = (word) => {
    if (selectedWords.includes(word)) {
      // Deselect
      setSelectedWords(selectedWords.filter(w => w !== word));
    } else {
      // Select (limit to 3)
      if (selectedWords.length >= 3) {
        setError('You can only select up to 3 words');
        setTimeout(() => setError(''), 3000);
        return;
      }
      setSelectedWords([...selectedWords, word]);
    }
  };

  const handleAddCustomWord = (e) => {
    e.preventDefault();

    if (!customWord.trim()) {
      return;
    }

    // Check if already added
    if (selectedWords.includes(customWord.trim())) {
      setError('This word is already selected');
      setTimeout(() => setError(''), 3000);
      setCustomWord('');
      return;
    }

    // Check limit
    if (selectedWords.length >= 3) {
      setError('You can only select up to 3 words');
      setTimeout(() => setError(''), 3000);
      return;
    }

    // Add custom word
    setSelectedWords([...selectedWords, customWord.trim()]);
    setCustomWord('');
  };

  const toggleArchetypeTexture = (word) => {
    if (selectedArchetypeTextures.includes(word)) {
      // Deselect
      setSelectedArchetypeTextures(selectedArchetypeTextures.filter(w => w !== word));
    } else {
      // Select
      setSelectedArchetypeTextures([...selectedArchetypeTextures, word]);
    }
  };

  const removeWord = (word) => {
    if (archetypeTextures.includes(word)) {
      // Remove from archetype selections
      setSelectedArchetypeTextures(selectedArchetypeTextures.filter(w => w !== word));
    } else {
      // Remove from custom selections
      setSelectedWords(selectedWords.filter(w => w !== word));
    }
  };

  const handleSaveAndContinue = async () => {
    const totalSelected = selectedArchetypeTextures.length + selectedWords.length;
    if (totalSelected === 0) {
      setError('Please select at least 1 word');
      setTimeout(() => setError(''), 3000);
      return;
    }

    setSaving(true);
    setError('');

    // Combine archetype and custom selections
    const allSelectedWords = [...selectedArchetypeTextures, ...selectedWords];

    try {
      const response = await fetch('/api/brand/save-brand-texture', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ selected_words: allSelectedWords })
      });

      if (response.ok) {
        navigate('/brand-voice-analysis');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to save brand texture');
      }
    } catch (err) {
      setError('Error saving brand texture: ' + err.message);
    }

    setSaving(false);
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
        <p className="mt-3">Loading...</p>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <BackToDashboard module="branding" />
      <Row className="justify-content-center">
        <Col md={12} lg={10}>
          <Card className="shadow">
            <Card.Body className="p-5">
              <h1 className="text-center mb-3">Brand Texture</h1>
              <p className="text-center text-muted mb-4">
                Select <strong>1-3 words</strong> that describe the textures that align with your brand.
              </p>

              {error && <Alert variant="danger">{error}</Alert>}

              {(selectedWords.length > 0 || selectedArchetypeTextures.length > 0) && (
                <Alert variant="info" className="text-center mb-4">
                  <strong>
                    {selectedArchetypeTextures.length > 0 && `${selectedArchetypeTextures.length} archetype texture${selectedArchetypeTextures.length > 1 ? 's' : ''}`}
                    {selectedArchetypeTextures.length > 0 && selectedWords.length > 0 && ' + '}
                    {selectedWords.length > 0 && `${selectedWords.length}/3 custom selections`}
                  </strong>
                  {selectedWords.length < 3 && ' - You can select more'}
                </Alert>
              )}

              {/* Archetype Textures Section */}
              {archetypeTextures.length > 0 && (
                <>
                  <h5 className="mb-3">
                    ✨ Recommended for {archetypeName} Archetype
                    <small className="text-muted d-block mt-1" style={{ fontSize: '0.85rem' }}>
                      Pre-selected based on your brand archetype - you can deselect if desired
                    </small>
                  </h5>
                  <Row className="g-3 mb-4">
                    {archetypeTextures.map((word, idx) => {
                      const isSelected = selectedArchetypeTextures.includes(word);
                      return (
                        <Col key={`arch-${idx}`} xs={6} md={4} lg={3}>
                          <Card
                            onClick={() => toggleArchetypeTexture(word)}
                            style={{
                              cursor: 'pointer',
                              border: isSelected ? '3px solid #198754' : '2px solid #dee2e6',
                              backgroundColor: isSelected ? '#d1e7dd' : 'white',
                              transition: 'all 0.2s',
                              height: '100%',
                              minHeight: '80px'
                            }}
                            className="h-100 d-flex align-items-center justify-content-center"
                          >
                            <Card.Body className="text-center p-3">
                              {isSelected && (
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: '5px',
                                    right: '5px',
                                    backgroundColor: '#198754',
                                    color: 'white',
                                    borderRadius: '50%',
                                    width: '24px',
                                    height: '24px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '14px',
                                    fontWeight: 'bold'
                                  }}
                                >
                                  ✓
                                </div>
                              )}
                              <p className={`mb-0 ${isSelected ? 'fw-bold text-success' : ''}`}>
                                {word}
                              </p>
                            </Card.Body>
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>
                </>
              )}

              {/* Custom Selection Section */}
              <h5 className="mb-3 mt-4">
                Additional Texture Options
                <small className="text-muted d-block mt-1" style={{ fontSize: '0.85rem' }}>
                  Select up to 3 more words to customize your brand texture
                </small>
              </h5>
              <Row className="g-3 mb-4">
                {textureOptions.map((word, idx) => {
                  const isSelected = selectedWords.includes(word);
                  return (
                    <Col key={idx} xs={6} md={4} lg={3}>
                      <Card
                        onClick={() => toggleWord(word)}
                        style={{
                          cursor: 'pointer',
                          border: isSelected ? '3px solid #0d6efd' : '2px solid #dee2e6',
                          backgroundColor: isSelected ? '#e7f1ff' : 'white',
                          transition: 'all 0.2s',
                          height: '100%',
                          minHeight: '80px'
                        }}
                        className="h-100 d-flex align-items-center justify-content-center"
                      >
                        <Card.Body className="text-center p-3">
                          {isSelected && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '5px',
                                right: '5px',
                                backgroundColor: '#0d6efd',
                                color: 'white',
                                borderRadius: '50%',
                                width: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '14px',
                                fontWeight: 'bold'
                              }}
                            >
                              ✓
                            </div>
                          )}
                          <p className={`mb-0 ${isSelected ? 'fw-bold text-primary' : ''}`}>
                            {word}
                          </p>
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>

              {/* Custom Words Section */}
              <Card className="mt-4" style={{ backgroundColor: '#f8f9fa' }}>
                <Card.Body>
                  <h6 className="mb-3">Add Your Own Words</h6>
                  <p className="text-muted small mb-3">
                    Don't see the perfect word? Add your own custom texture descriptors.
                  </p>

                  {/* Show selected words as badges */}
                  {(selectedWords.length > 0 || selectedArchetypeTextures.length > 0) && (
                    <div className="mb-3">
                      <strong className="d-block mb-2">Selected Words:</strong>
                      {selectedArchetypeTextures.map((word, idx) => (
                        <Badge
                          key={`badge-arch-${idx}`}
                          bg="success"
                          className="me-2 mb-2"
                          style={{ fontSize: '0.9rem', padding: '8px 12px', cursor: 'pointer' }}
                          onClick={() => removeWord(word)}
                          title="Archetype texture"
                        >
                          ✨ {word} <span style={{ marginLeft: '5px' }}>×</span>
                        </Badge>
                      ))}
                      {selectedWords.map((word, idx) => (
                        <Badge
                          key={`badge-custom-${idx}`}
                          bg="primary"
                          className="me-2 mb-2"
                          style={{ fontSize: '0.9rem', padding: '8px 12px', cursor: 'pointer' }}
                          onClick={() => removeWord(word)}
                          title="Custom selection"
                        >
                          {word} <span style={{ marginLeft: '5px' }}>×</span>
                        </Badge>
                      ))}
                      <div className="text-muted small mt-2">
                        Click a word to remove it (✨ = archetype recommendation)
                      </div>
                    </div>
                  )}

                  {/* Custom word input */}
                  <Form onSubmit={handleAddCustomWord}>
                    <Form.Group>
                      <div className="d-flex gap-2">
                        <Form.Control
                          type="text"
                          placeholder="Enter a custom texture word..."
                          value={customWord}
                          onChange={(e) => setCustomWord(e.target.value)}
                          disabled={selectedWords.length >= 3}
                        />
                        <Button
                          type="submit"
                          variant="outline-primary"
                          disabled={!customWord.trim() || selectedWords.length >= 3}
                        >
                          Add
                        </Button>
                      </div>
                      {selectedWords.length >= 3 && (
                        <Form.Text className="text-warning">
                          Maximum of 3 words reached. Remove a word to add another.
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Form>
                </Card.Body>
              </Card>

              <div className="d-flex justify-content-between mt-4">
                <Button
                  variant="outline-secondary"
                  onClick={() => navigate('/dashboard/branding')}
                >
                  ← Back to Dashboard
                </Button>
                <Button
                  variant="primary"
                  size="lg"
                  disabled={saving || selectedWords.length === 0}
                  onClick={handleSaveAndContinue}
                >
                  {saving ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Saving...
                    </>
                  ) : (
                    'Save & Continue →'
                  )}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default BrandTexture;
